import React, { useRef, useEffect, useContext } from 'react';
import classnames from 'classnames';
import omitBy from 'lodash/omitBy';
import isString from 'lodash/isString';
import PlainButtonOrLink from '../../../../Buttons/PlainButtonOrLink';
import { DropdownOption } from '../../../types';
import ArrowRightIcon from '../../../../Icons/ArrowRight';
import { IconSize } from '../../../../Icons/IconWrap';
import Flex from '../../../../Flex';
import ListItemButton from '../../../../List/ListItem/dsPresets/ListItemButton';
import BetaBadge from '../../../../BetaBadge';
import StandardMenuSubmenu from '../StandardMenuSubmenu';
import StandardMenuContext from '../context';
import s from './StandardMenuOption.scss';

/**
 * A parent of StandardMenuOption could be a multitude of roles. Each parent role may require a certain role be applied to this component.
 *
 * This returns the correct role for the supplied `parentRole`.
 */
function getRoleBasedOnParent(
  // The `string | undefined` part here is to get around some typing issues
  parentRole: 'listbox' | 'menu' | string | undefined
) {
  switch (parentRole) {
    case 'listbox':
      return 'option';
    case 'menu':
      return 'menuitem';
    default:
      break;
  }
}

export interface Props {
  id: string;
  item: DropdownOption;
  withHoverEffect?: boolean;
  hideCheckIcon?: boolean;
  className?: string;
  /**
   * Should this be focused when it's rendered next? Wrapped in a useEffect so it will only
   * trigger when this is flipped between true and false. Subsequent renders will ignore this.
   */
  focused?: boolean;
}

const StandardMenuOption: React.FC<Props> = ({
  id,
  item,
  item: {
    icon: itemIcon,
    iconClassName,
    suffix: suffixProp,
    onClick,
    onMouseEnter,
    disabled,
    danger,
    className: itemClassName,
    value,
    highlighted,
    selected,
    label,
    subLabel,
    boldLabel,
    biData,
    dataHook,
    tabIndex,
    link,
    target,
    title,
    submenu,
    subMenuPreferredSide,
    estimatedSubmenuWidth,
    minimumHeightNeededForSubmenu,
  },
  hideCheckIcon,
  withHoverEffect,
  className,
  focused,
}) => {
  const { role: parentRole } = useContext(StandardMenuContext);
  let icon: React.ReactNode = null;
  if (itemIcon) {
    icon = React.cloneElement(itemIcon, {
      className: iconClassName,
    });
  }
  const itemRef = useRef<HTMLElement | null>(null);
  const hasActiveSubmenu = submenu && !disabled;

  useEffect(() => {
    if (itemRef.current && focused) {
      itemRef.current.focus({ preventScroll: true });
    }
  }, [itemRef, focused]);

  const prefix = icon;

  const suffix = submenu ? (
    <ArrowRightIcon size={IconSize.SMALLER} className={s['submenu-arrow']} />
  ) : (
    suffixProp
  );

  const content = (
    <ListItemButton
      href={link}
      target={target}
      className={itemClassName}
      fillContainerWidth
      disabled={disabled}
      isDangerous={danger}
      prefix={prefix}
      suffix={suffix}
      isLabelBold={selected || boldLabel}
      isHighlighted={highlighted}
      biData={hasActiveSubmenu ? biData : undefined}
      label={
        item.complexLabel || (
          <Flex alignItems="center">
            {item.label}
            {item.newTag && (
              <BetaBadge
                size="small"
                variant={isString(item.newTag) ? item.newTag : 'green'}
                badgeType="new"
                className={s['new-tag']}
              />
            )}
          </Flex>
        )
      }
      subLabel={subLabel}
    />
  );

  // omitBy to prevent undefined values from overwriting props that we need.
  const sharedProps = omitBy(
    {
      id,
      innerRef: node => {
        itemRef.current = node;
      },
      className: classnames(
        s['menu-item'],
        // TODO: address via 6.1 ?
        highlighted && s['highlighted'],
        className
      ),
      value,
      disabled,
      selected,
      label: typeof label === 'string' ? label : undefined,
      onClick: disabled ? undefined : onClick,
      onMouseEnter,
      biData: hasActiveSubmenu ? undefined : biData,
      // We don't want to tab into items individually, the wrapping StandardMenuContent will manage
      // focus itself.
      tabIndex: tabIndex === undefined ? -1 : tabIndex,
      children: content,
      title,
      role: getRoleBasedOnParent(parentRole),
      'data-hook': dataHook,
      'data-testid': 'menu-option',
      // Undefined on false to avoid some extra text being read out in VoiceOver + Chrome
      'aria-selected': selected ? true : undefined,
      'aria-disabled': disabled,
      'data-selected': selected ? 'selected' : undefined,
    },
    keyValue => keyValue === undefined
  );

  if (link) {
    return (
      <div
        ref={node => {
          itemRef.current = node;
        }}
        {...sharedProps}
      />
    );
  } else if (submenu && !disabled) {
    // A11Y TODO: Figure out what role these should have
    // Should likely be the usual `role="getRoleBasedOnParent(...)"` with an `aria-haspopup`
    // https://www.digitala11y.com/option-role/
    return (
      <StandardMenuSubmenu
        submenu={submenu}
        preferredSide={subMenuPreferredSide}
        estimatedSubmenuWidth={estimatedSubmenuWidth}
        minimumHeightNeeded={minimumHeightNeededForSubmenu}
        {...sharedProps}
      />
    );
  } else {
    return (
      <PlainButtonOrLink
        innerRef={node => {
          itemRef.current = node;
        }}
        isFakeButton
        {...sharedProps}
      />
    );
  }
};
StandardMenuOption.displayName = 'StandardMenuOption';

export default StandardMenuOption;
